import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

import {
  BasicResponse,
  CustomerProfileSummary,
  FanAuthLoginRequest,
  FanAuthLoginResponse,
  FanAuthPasswordResetRequest,
  FanAuthRegisterRequest,
  FanAuthRegisterResponse,
  DocumentInput,
  UserWithGameDetails,
} from "../../shared/interfaces";
import { PATH } from "../../shared/constants";
import {
  analyticsTrackingAllowed,
  readStoredAnalyticsConsent,
  syncAnalyticsConsent,
} from "../../utils/analyticsConsent";

export const addUserRequest = async (
  request: UserWithGameDetails,
): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(`${PATH.API}/${PATH.USER}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getCustomerProfileRequest = async (
  profileId: string,
): Promise<CustomerProfileSummary> => {
  try {
    const response = await instance.get<CustomerProfileSummary>(`${PATH.API}/${PATH.USER}/profile/${profileId}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteProfileRequest = async (profileId: string): Promise<{ deleted: boolean }> => {
  try {
    const response = await instance.delete<{ deleted: boolean }>(
      `${PATH.API}/${PATH.USER}/profile/${profileId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface GetMeResponse {
  profile?: CustomerProfileSummary;
  lastSession?: any;
}

export const getMeRequest = async (idToken: string): Promise<GetMeResponse> => {
  try {
    const response = await instance.get<GetMeResponse>(`${PATH.API}/${PATH.USER}/me`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getAvatarUploadUrlRequest = async (
  idToken: string,
  contentType = "image/jpeg",
): Promise<{ url: string; fields: Record<string, string>; s3Key: string }> => {
  try {
    const response = await instance.post<{ url: string; fields: Record<string, string>; s3Key: string }>(
      `${PATH.API}/${PATH.USER}/me/avatar-upload-url`,
      { contentType },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateAvatarRequest = async (idToken: string, s3Key: string): Promise<void> => {
  try {
    await instance.put(
      `${PATH.API}/${PATH.USER}/me/avatar`,
      { s3Key },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateProfileGameContextRequest = async (
  idToken: string,
  gameId: string,
  context: string,
): Promise<void> => {
  try {
    await instance.put(
      `${PATH.API}/${PATH.USER}/me/profile/game-context`,
      { gameId, context },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const shareWithFriendRequest = async (
  idToken: string,
  gameId: string,
  friendEmail: string,
): Promise<{ success: boolean; sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }> }> => {
  try {
    const response = await instance.put<{ success: boolean; sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }> }>(
      `${PATH.API}/${PATH.USER}/me/profile/share-with-friend`,
      { gameId, friendEmail },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const shareWithFriendByProfileIdRequest = async (
  profileId: string,
  gameId: string,
  friendEmail: string,
): Promise<{ success: boolean; sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }> }> => {
  try {
    const response = await instance.put<{ success: boolean; sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }> }>(
      `${PATH.API}/${PATH.USER}/profile/share-with-friend`,
      { profileId, gameId, friendEmail },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const trackImagesViewedRequest = async (
  profileId: string,
  count = 1,
  registrationId?: string,
): Promise<{ success: boolean; imagesViewedCount: number }> => {
  try {
    const response = await instance.put<{ success: boolean; imagesViewedCount: number }>(
      `${PATH.API}/${PATH.USER}/profile/images-viewed`,
      {
        profileId,
        count,
        ...(registrationId?.trim() ? { registrationId: registrationId.trim() } : {}),
      },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

const MOMENTOS_SEEN_CHUNK = 400;

/** Records distinct momento fingerprints seen for this seat+game (idempotent). */
export const recordMomentosSeenRequest = async (
  registrationId: string,
  gameId: string,
  fingerprints: string[],
): Promise<{ success: boolean; uniqueCount: number; added: number }> => {
  try {
    const response = await instance.put<{
      success: boolean;
      uniqueCount: number;
      added: number;
    }>(
      `${PATH.API}/${PATH.USER}/profile/momentos-seen`,
      { registrationId, gameId, fingerprints },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

/** Sends all fingerprints in chunks (server max 400 per request). */
export const recordMomentosSeenAllChunks = async (
  registrationId: string,
  gameId: string,
  fingerprints: string[],
): Promise<void> => {
  if (fingerprints.length === 0) return;
  const allowed = await analyticsTrackingAllowed();
  if (!allowed) return;
  const stored = readStoredAnalyticsConsent();
  if (stored?.status === "accepted") {
    try {
      await syncAnalyticsConsent(stored);
    } catch {
      /* server still checks the saved consent */
    }
  }
  const chunks: string[][] = [];
  for (let i = 0; i < fingerprints.length; i += MOMENTOS_SEEN_CHUNK) {
    chunks.push(fingerprints.slice(i, i + MOMENTOS_SEEN_CHUNK));
  }
  await chunks.reduce(
    (previous, slice) => previous.then(() => recordMomentosSeenRequest(
      registrationId,
      gameId,
      slice,
    )),
    Promise.resolve(),
  );
};

export const registerFanAuthAccountRequest = async (
  request: FanAuthRegisterRequest,
): Promise<FanAuthRegisterResponse> => {
  try {
    const response = await instance.post<FanAuthRegisterResponse>(
      `${PATH.API}/${PATH.USER}/${PATH.FAN_AUTH}/${PATH.REGISTER}`,
      request,
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const loginFanRequest = async (
  request: FanAuthLoginRequest,
): Promise<FanAuthLoginResponse> => {
  try {
    const response = await instance.post<FanAuthLoginResponse>(
      `${PATH.API}/${PATH.USER}/${PATH.FAN_AUTH}/${PATH.LOGIN}`,
      request,
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const requestFanPasswordReset = async (
  request: FanAuthPasswordResetRequest,
): Promise<BasicResponse> => {
  try {
    const response = await instance.post<BasicResponse>(
      `${PATH.API}/${PATH.USER}/${PATH.FAN_AUTH}/${PATH.RESET_PASSWORD}`,
      request,
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getLastSession = async (
  email: string,
): Promise<any> => {
  try {
    const response = await instance.get<any>(`${PATH.API}/${PATH.USER}/${PATH.GET_LAST_SESSION}?email=${email}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateRegistrationSessionRequest = async (
  _id: string,
  session: Record<string, unknown>,
): Promise<any> => {
  try {
    const response = await instance.put<any>(`${PATH.API}/${PATH.USER}/${PATH.UPDATE_USER_SESSION}/${_id}`, {
      session,
    });

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateLastSessionDate = async (
  _id: string,
): Promise<any> => {
  try {
    const response = await instance.put<any>(`${PATH.API}/${PATH.USER}/${PATH.UPDATE_USER_SESSION}/${_id}`, {
      session: {
        updated: new Date(),
      },
    });

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const payForTheGame = async (
  email: string, gameId: string,
): Promise<BasicResponse> => {
  try {
    const response = await instance.post<BasicResponse>(`${PATH.API}/${PATH.PAYMENT}/${PATH.ADD_UPDATE_PAYMENT}`, {
      email,
      gameId,
      paid: true,
    });

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addPendingPayment = async (
  email: string, gameId: string,
): Promise<BasicResponse> => {
  try {
    const response = await instance.post<BasicResponse>(`${PATH.API}/${PATH.PAYMENT}/${PATH.ADD_PENDING_PAYMENT}`, {
      email,
      gameId,
      pending: true,
    });

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getDownloadPaidStatus = async (
  email: string,
  gameId: string,
  section: string,
  row: string,
  seat: string,
): Promise<boolean> => {
  try {
    const response = await instance.post<boolean>(`${PATH.API}/${PATH.PAYMENT}/${PATH.CHECK_GAME_IS_PAID}`, {
      email,
      gameId,
      section,
      row,
      seat,
    });

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
